import { CircleAlert, LifeBuoy, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatBytes } from '@/core/time/format';
import { useRecordingStore } from '@/state/recording-store';
import { ConfirmDialog } from '../Dialog';
import { Button, IconButton, Notice, ProgressBar } from '../primitives';
import { problemKey } from './media';

/**
 * Recordings an earlier visit left on the device (a crash, a closed tab), offered
 * back first thing on the home page: never lose the person's work.
 */
export function LeftoverRecordings() {
  const { t, i18n } = useTranslation();
  const leftovers = useRecordingStore((state) => state.leftovers);
  const recovering = useRecordingStore((state) => state.recovering);
  const { recover, deleteLeftover, refreshLeftovers } = useRecordingStore.getState();
  // It may be the only copy of someone's work: deleting asks first.
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    void refreshLeftovers();
  }, [refreshLeftovers]);

  if (leftovers.length === 0) return null;
  const date = new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  const describe = (leftover: (typeof leftovers)[number]) => t('recovery.item', { date: date.format(leftover.modified), size: formatBytes(leftover.size, i18n.language) });
  const target = leftovers.find((leftover) => leftover.id === deleting);
  return (
    <section aria-labelledby="leftovers-title" className="rounded-2xl bg-accent-soft px-4 py-3.5 ring-1 ring-accent-strong/40 ring-inset">
      <h2 id="leftovers-title" className="flex items-center gap-2 text-[15px] font-semibold text-fg">
        <LifeBuoy size={16} className="shrink-0 text-accent-text" />
        {t('recovery.title')}
      </h2>
      <ul className="mt-2 divide-y divide-line">
        {leftovers.map((leftover) => {
          const busy = recovering?.id === leftover.id;
          return (
            <li key={leftover.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="text-sm text-fg tabular">{describe(leftover)}</p>
                {leftover.unfinished && !busy && <p className="text-[13px] text-fg-muted">{t('recovery.unfinishedHint')}</p>}
                {busy && (
                  <div className="mt-1.5 max-w-60 space-y-1.5">
                    <p className="text-[13px] text-fg-muted tabular">{t('recovery.repairing', { percent: Math.round(recovering.progress * 100) })}</p>
                    <ProgressBar value={recovering.progress} />
                  </div>
                )}
              </div>
              <span className="flex items-center gap-1">
                <Button size="sm" variant="secondary" disabled={recovering !== null} onClick={() => void recover(leftover.id)}>
                  {t('recovery.open')}
                </Button>
                <IconButton label={t('recovery.delete')} disabled={recovering !== null} onClick={() => setDeleting(leftover.id)} className="hover:text-danger">
                  <Trash2 size={16} />
                </IconButton>
              </span>
            </li>
          );
        })}
      </ul>
      <ConfirmDialog
        open={target !== undefined}
        title={t('recovery.deleteTitle')}
        body={target ? t('recovery.deleteBody', { item: describe(target) }) : ''}
        confirmLabel={t('recovery.delete')}
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (target) void deleteLeftover(target.id);
          setDeleting(null);
        }}
      />
    </section>
  );
}

/** Why the last recording attempt didn't work, until the person dismisses it. */
export function RecordingProblem() {
  const { t } = useTranslation();
  const problem = useRecordingStore((state) => state.problem);
  const dismiss = useRecordingStore((state) => state.dismissProblem);
  // Device problems are shown beside their control in the setup instead.
  if (!problem || (problem.kind === 'capture' && problem.source !== 'screen' && problem.problem !== 'not-found')) return null;
  return (
    <Notice tone="danger" icon={<CircleAlert size={15} />}>
      <div className="flex items-start gap-3">
        <p className="min-w-0 flex-1">{t(problemKey(problem))}</p>
        <button type="button" onClick={dismiss} aria-label={t('common.close')} className="-m-1 rounded-md p-1 opacity-70 hover:opacity-100">
          <X size={14} />
        </button>
      </div>
    </Notice>
  );
}
